/**
 * 音声認識ハルシネーション（無音区間に紛れ込む定型句）の決定論フィルタ — **正本**
 *
 * PRODUCT_DEV-475 / 551: 「ご視聴ありがとうございました」等の動画・字幕由来の定型句が
 * ASR 出力に混入する。LLM を通らないテキスト（rawTranscript / dialogueLog / GateKeeper 入力）は
 * プロンプトでは消えないため、ここで決定論的に除去する。
 *
 * PRODUCT_DEV-776: unizo（`apps/unizo/api/utils/hallucinationFilter.ts`）と
 * survey（`packages/shared-domain/survey/voice.ts`）で表を二重管理し「増えたら両方に足す」
 * 運用になっていたものを、このモジュールへ集約した。現場報告で増えたら**ここだけ**に足す。
 *
 * **除去は「行ごと削除」ではなく「行内の該当句だけ削除」**（PRODUCT_DEV-776）。
 * 実データで `お子さんたくさんいらっしゃるので、これからもご清聴ありがとうございました。` のように
 * 実発話の末尾へ定型句が接着する例が観測されており、行ごと落とすと実発話まで消える。
 * 句だけ削って残りが実質空になった行は、従来どおり行ごと除去する。
 *
 * そのため**パターンは「句として完結する形」で書く**。核部分だけを登録すると
 * 「本日はご覧いただきありがとうございます」→「本日はす」のような断片が残る
 * （これが従来 行ごと削除にしていた理由）。接頭辞・語尾の揺れはパターン側で吸収する。
 */

/**
 * 除去対象のハルシネーション。正規表現ソース（`g` フラグ無しで書く）。
 * いずれも実商談・実調査には出現しない動画/字幕由来の定型句。
 */
export const HALLUCINATION_PATTERN_SOURCES: readonly string[] = [
  // 視聴/清聴の感謝系。「それではご視聴〜」「最後までご清聴いただき誠にありがとうございました」等。
  "(?:それでは|最後まで|本日は|今日は)?[\\s、]*ご(?:視聴|清聴)(?:いただき)?(?:誠に)?ありがとうございま(?:す|した)",
  // PRODUCT_DEV-551: 「本日はご覧いただきありがとうございます」系。
  "(?:それでは|最後まで|本日は|今日は)?[\\s、]*ご覧いただき(?:誠に)?ありがとうございま(?:す|した)",
  // PRODUCT_DEV-475 追加報告: 動画アウトロ系／字幕・文字起こし系。
  "(?:それでは)?[\\s、]*次の動画で(?:また)?お会いしましょう",
  "日本語字幕を(?:オン|ON|on)にしてください",
  "日本語以外の(?:発話|音声)は文字起こし(?:を)?しないでください",
].map((src) => `${src}[。．.!！]*`);

/** 判定用（`g` 無し＝`lastIndex` に依存しない）。 */
const MATCHERS = HALLUCINATION_PATTERN_SOURCES.map((src) => new RegExp(src));
/** 除去用（1 行に複数回出ることがあるため `g`）。 */
const REMOVERS = HALLUCINATION_PATTERN_SOURCES.map(
  (src) => new RegExp(src, "g"),
);

/** 記号・空白しか残っていない＝実質空、と見なすための判定。 */
const ONLY_PUNCTUATION = /^[\s、。，．,.\-–—・…!！?？"'「」『』()（）]*$/;

/**
 * 1発話（1行）にハルシネーションが含まれるか判定する。
 */
export function isHallucinationUtterance(text: string): boolean {
  return MATCHERS.some((re) => re.test(text));
}

/**
 * 行内のハルシネーション該当句だけを取り除く（前後の実発話は残す）。
 * 何も該当しなければ元の文字列をそのまま返す（呼び出し側で同一性を判定できる）。
 */
export function stripHallucinationSpans(text: string): string {
  let out = text;
  for (const re of REMOVERS) {
    re.lastIndex = 0;
    out = out.replace(re, "");
  }
  if (out === text) return text;
  // 句を削った跡に残る宙ぶらりんの読点・空白を落とす（「これからも、」→「これからも」）。
  return out.replace(/[\s、，]+$/, "").replace(/^[\s]+/, "");
}

/** 句を削った結果が実質空（記号だけ／空文字）か。 */
export function isBlankAfterStrip(text: string): boolean {
  return ONLY_PUNCTUATION.test(text);
}

/**
 * ユーザ発話テキスト（改行区切り）からハルシネーションを除去する。
 * rawTranscript / speed モードの Whisper 出力用。
 *
 * - 行内の該当句を削る
 * - 削った結果が実質空になった行は落とす
 * - 該当が無い行は 1 文字も変えない
 */
export function stripHallucinationUtterances(text: string): string {
  if (!text) return text;
  const out: string[] = [];
  for (const line of text.split("\n")) {
    const stripped = stripHallucinationSpans(line);
    if (stripped !== line && isBlankAfterStrip(stripped)) continue;
    out.push(stripped);
  }
  return out.join("\n");
}

/** 対話ログの `[timestamp] User: …` 行。話者ラベルまでを prefix として捕捉する。 */
const DIALOGUE_USER_LINE = /^(\[.+?\]\s+User:\s*)(.*)$/;
/** 話者ラベル付きの行（User 以外＝AI 応答など）。 */
const DIALOGUE_SPEAKER_LINE = /^\[.+?\]\s+\S+?:/;

/**
 * `DIALOGUE_USER_LINE` のマッチ結果から、本文だけを除去処理した行を組み立てる。
 * 本文が実質空になったら行ごと落とす意味で `null` を返す。
 */
function cleanUserLine(match: RegExpMatchArray): string | null {
  const [, prefix = "", body = ""] = match;
  const stripped = stripHallucinationSpans(body);
  if (stripped !== body && isBlankAfterStrip(stripped)) return null;
  return `${prefix}${stripped}`;
}

/**
 * 対話ログ（`[timestamp] User: ...` / `[timestamp] AI: ...` 形式）から
 * ハルシネーションを除去する。
 * - User 行のみ本文を対象にする（AI 行・フォーマット非マッチ行はそのまま保持＝
 *   AI 応答の正当な文言を壊さない）
 * - 本文が実質空になったら行ごと除去する
 *
 * dialogueLog 用。
 */
export function stripHallucinationFromDialogueLog(log: string): string {
  if (!log) return log;
  const out: string[] = [];
  for (const line of log.split("\n")) {
    const match = line.match(DIALOGUE_USER_LINE);
    if (!match) {
      out.push(line); // AI 行・非マッチ行は保持
      continue;
    }
    const cleaned = cleanUserLine(match);
    if (cleaned !== null) out.push(cleaned);
  }
  return out.join("\n");
}

/**
 * GateKeeper へ渡す会話ログからハルシネーションを除去する（PRODUCT_DEV-776）。
 *
 * `completeMeeting()` には録音モードの異なるログが同じ引数で入ってくるため、
 * 形式を**行ごとに**判定して上の 2 関数と同じ規則を当てる:
 * - `[timestamp] User: …` 行 … 本文だけを対象にする
 * - 話者ラベル付きのその他の行（AI 応答）… 保持する
 * - それ以外の行（自由記録の Whisper 出力・議事録本文）… 行全体を対象にする
 *
 * 既に除去済みのログを再度通しても結果は変わらない（冪等）。
 */
export function stripHallucinationFromConversationLog(log: string): string {
  if (!log) return log;
  const out: string[] = [];
  for (const line of log.split("\n")) {
    const match = line.match(DIALOGUE_USER_LINE);
    if (match) {
      const cleaned = cleanUserLine(match);
      if (cleaned !== null) out.push(cleaned);
      continue;
    }
    if (DIALOGUE_SPEAKER_LINE.test(line)) {
      out.push(line); // AI 行など話者付きの非 User 行は保持
      continue;
    }
    const stripped = stripHallucinationSpans(line);
    if (stripped !== line && isBlankAfterStrip(stripped)) continue;
    out.push(stripped);
  }
  return out.join("\n");
}
